import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Tooltip,
  useTheme,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Edit,
  Delete,
  Visibility,
  Event,
  Today,
  Schedule,
  LocationOn,
  Person,
  MoreVert,
  Refresh,
  CalendarToday,
  ViewDay,
  ViewWeek,
  ViewModule,
  ViewAgenda,
  Calculate,
  MeetingRoom,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiError';
import { getCalendarUserColor } from '../../utils/calendarUserColors';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import CalendarDialog from '../../components/Calendar/CalendarDialog';
import CalendarFilters from '../../components/Calendar/CalendarFilters';
import WeekView from '../../components/Calendar/WeekView';
import MonthView from '../../components/Calendar/MonthView';
import MiniCalendar from '../../components/Calendar/MiniCalendar';
import DateCalculatorDialog from '../../components/Calendar/DateCalculatorDialog';
import ResourceBookingView from '../../components/Calendar/ResourceBookingView';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';
interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  eventType: 'client_meeting' | 'court_hearing' | 'deposition' | 'mediation' | 'consultation' | 'filing_deadline' | 'document_review' | 'billing_review' | 'staff_meeting' | 'training' | 'other';
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
  resourceId?: string | null;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reminderEnabled: boolean;
  reminderTime: number;
  reminderSent: boolean;
  notes: string | null;
  attendees: any[];
  recurrence: any;
  case: { id: string; caseNumber: string; title: string; } | null;
  client: { id: string; name: string; } | null;
  task: { id: string; title: string; status: string; } | null;
  creator: { id: string; name: string; email: string; } | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface CalendarResponse {
  success: boolean;
  data: {
    events: CalendarEvent[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

interface Filters {
  search: string;
  eventType: string;
  status: string;
  priority: string;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  allDay: string;
}

const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'view' | 'edit'>('create');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    eventType: '',
    status: '',
    priority: '',
    startDateFrom: '',
    startDateTo: '',
    endDateFrom: '',
    endDateTo: '',
    allDay: '',
  });
  const [viewMode, setViewMode] = useState<'list' | 'week' | 'month' | 'resources'>('list');
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [eventPrefill, setEventPrefill] = useState<Partial<CalendarEvent> | null>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());

  // Fetch calendar events
  const { data: calendarData, isLoading, error, refetch } = useQuery({
    queryKey: ['calendar', user?.tenantId, filters],
    queryFn: async () => {
      console.log('=== CALENDAR API CALL START ===');
      console.log('Token exists:', !!getAuthToken());
      console.log('Token preview:', getAuthToken()?.substring(0, 20) + '...');
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      console.log('Fetching calendar events with params:', params.toString());

      const response = await fetch(`/api/calendar?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const message = await getApiErrorMessage(response, 'Failed to fetch calendar events.');
        console.error('Calendar API error:', message);
        throw new Error(message);
      }
      
      const result = await response.json();
      console.log('Calendar events fetched:', result);
      console.log('Events array:', result.data?.events);
      console.log('Events count:', result.data?.events?.length);
      console.log('Full response structure:', JSON.stringify(result, null, 2));
      return result as CalendarResponse;
    },
  });

  // Log any errors
  if (error) {
    console.error('Calendar query error:', error);
  }

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await fetch(`/api/calendar/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete event');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success('Event deleted successfully');
      setDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to delete event');
      console.error('Delete error:', error);
    },
  });

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setEventPrefill(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleCreateFromCalculator = (prefill: {
    title: string;
    eventType: 'filing_deadline';
    startDate: string;
    endDate: string;
    allDay: boolean;
    notes: string;
  }) => {
    setSelectedEvent(null);
    setEventPrefill(prefill);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleResourceSlotClick = (resourceId: string, start: Date, end: Date) => {
    setSelectedEvent(null);
    setEventPrefill({
      title: '',
      eventType: 'client_meeting',
      startDate: format(start, "yyyy-MM-dd'T'HH:mm"),
      endDate: format(end, "yyyy-MM-dd'T'HH:mm"),
      resourceId,
      allDay: false,
    } as Partial<CalendarEvent>);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleViewEvent = (event: CalendarEvent) => {
    console.log('Viewing event:', event);
    setSelectedEvent(event);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      deleteEventMutation.mutate(eventId);
    }
  };

  const handleSaveEvent = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar'] });
    setDialogOpen(false);
  };

  const handleFilterChange = (newFilters: Partial<Filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const getEventColor = (event: CalendarEvent) =>
    getCalendarUserColor(event.creator, theme.palette.grey[500]);

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: theme.palette.success.main,
      medium: theme.palette.warning.main,
      high: theme.palette.error.main,
      urgent: theme.palette.error.dark,
    };
    return colors[priority] || theme.palette.grey[500];
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      scheduled: theme.palette.info.main,
      confirmed: theme.palette.success.main,
      cancelled: theme.palette.error.main,
      completed: theme.palette.success.dark,
      no_show: theme.palette.warning.main,
    };
    return colors[status] || theme.palette.grey[500];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'HH:mm');
    } catch (error) {
      return 'Invalid time';
    }
  };

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="calendar events"
          onRetry={() => refetch()}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Calendar
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Calculate />}
            onClick={() => setCalculatorOpen(true)}
          >
            Date Calculator
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['calendar'] })}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateEvent}
          >
            New Event
          </Button>
        </Box>
      </Box>

      {/* Mini Calendar - COMPACT VERSION */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
        <MiniCalendar
          events={calendarData?.data.events || []}
          currentDate={currentDate}
          onEventClick={handleViewEvent}
          onEventEdit={handleEditEvent}
          onEventView={handleViewEvent}
          onDateChange={setCurrentDate}
        />
      </Box>

      {/* View Mode Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={viewMode}
          onChange={(_, newValue) => setViewMode(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            value="list"
            label="List View"
            icon={<ViewAgenda />}
            iconPosition="start"
          />
          <Tab
            value="week"
            label="Week View"
            icon={<ViewWeek />}
            iconPosition="start"
          />
          <Tab
            value="month"
            label="Month View"
            icon={<ViewModule />}
            iconPosition="start"
          />
          <Tab
            value="resources"
            label="Rooms & Zoom"
            icon={<MeetingRoom />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {viewMode !== 'resources' && (
        <CalendarFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      )}

      {viewMode === 'resources' ? (
        <Box sx={{ flex: 1, minHeight: '400px', overflow: 'auto', p: 2 }}>
          <ResourceBookingView
            currentDate={currentDate}
            onSlotClick={handleResourceSlotClick}
            onBookingClick={(booking) => {
              const full = calendarData?.data?.events?.find((e) => e.id === booking.id);
              if (full) handleViewEvent(full);
            }}
          />
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ 
          flex: 1, 
          minHeight: '400px',
          overflow: 'auto', 
          backgroundColor: '#f5f5f5', 
          border: '1px solid #ddd', 
          p: 2,
          borderRadius: 1
        }}>
          {viewMode === 'list' && (
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
            Events List
          </Typography>
          )}
          
          <Grid container spacing={2}>
            {/* Main Calendar Content */}
            {viewMode === 'list' && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Events ({calendarData?.data.events?.length || 0})
                  </Typography>

                    <List>
                      {calendarData?.data.events?.map((event, index) => {
                        console.log(`Processing event ${index}:`, event.title);
                        
                        // Check if event has required properties
                        if (!event.title || !event.id) {
                          console.error('Event missing required properties:', event);
                          return null;
                        }
                        
                        return (
                          <React.Fragment key={event.id}>

                            <ListItem
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                mb: 1,
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                            <ListItemIcon>
                              <Event sx={{ color: getEventColor(event) }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <>
                                  <Typography variant="subtitle1" fontWeight="bold" component="span">
                                    {event.title}
                                  </Typography>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                    <Chip
                                      label={event.eventType.replace('_', ' ')}
                                      size="small"
                                      sx={{ backgroundColor: getEventColor(event), color: 'white' }}
                                    />
                                    <Chip
                                      label={event.priority}
                                      size="small"
                                      sx={{ backgroundColor: getPriorityColor(event.priority), color: 'white' }}
                                    />
                                    <Chip
                                      label={event.status}
                                      size="small"
                                      sx={{ backgroundColor: getStatusColor(event.status), color: 'white' }}
                                    />
                                  </div>
                                </>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {event.description}
                                  </Typography>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <Today fontSize="small" />
                                      <Typography variant="body2" component="span">
                                        {formatDate(event.startDate)}
                                      </Typography>
                                    </div>
                                    {event.location && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <LocationOn fontSize="small" />
                                        <Typography variant="body2" component="span">
                                          {event.location}
                                        </Typography>
                                      </div>
                                    )}
                                    {event.creator && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Person fontSize="small" />
                                        <Typography variant="body2" component="span">
                                          {event.creator.name}
                                        </Typography>
                                      </div>
                                    )}
                                  </div>
                                </>
                              }
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewEvent(event)}
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEditEvent(event)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteEvent(event.id)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </ListItem>
                          {index < (calendarData?.data.events.length || 0) - 1 && <Divider />}
                        </React.Fragment>
                                              );
                      })}
                      
                      {/* Debug: Show if no events were rendered */}
                      {(!calendarData?.data.events || calendarData.data.events.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                          No events found in data
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {viewMode === 'week' && (
              <Grid item xs={12}>
                <WeekView
                  events={calendarData?.data.events || []}
                  currentDate={currentDate}
                  onEventClick={handleViewEvent}
                  onEventEdit={handleEditEvent}
                  onEventView={handleViewEvent}
                />
              </Grid>
            )}

            {viewMode === 'month' && (
              <Grid item xs={12}>
                <MonthView
                  events={calendarData?.data.events || []}
                  currentDate={currentDate}
                  onEventClick={handleViewEvent}
                  onEventEdit={handleEditEvent}
                  onEventView={handleViewEvent}
                  onDateChange={setCurrentDate}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <CalendarDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEventPrefill(null);
        }}
        event={selectedEvent}
        mode={dialogMode}
        onSave={() => {
          handleSaveEvent();
          setEventPrefill(null);
          queryClient.invalidateQueries({ queryKey: ['resource-bookings'] });
        }}
        initialPrefill={eventPrefill}
      />

      <DateCalculatorDialog
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        onCreateDeadline={handleCreateFromCalculator}
      />

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 80, right: 16 }}
        onClick={handleCreateEvent}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default CalendarPage; 